import React from 'react';

interface Props {
  onSubmit: (action: 'CORRECTED' | 'APPROVED' | 'REJECTED') => void;
  submitting: boolean;
  balanced: boolean;
  isDirty: boolean;
  hasSavedCorrections: boolean;
  fromPortal?: boolean;
  onBack?: () => void;
  readOnly?: boolean;
  invoiceStatus?: string;
  vendorMissing?: boolean;  // true for OTHER invoices where vendor_number is still null
  workflowType?: string;    // show Troubleshoot button only for 'OTHER'
  onTroubleshoot?: () => void;
  headerIsEditing?: boolean;  // true when header edit panel is open (unsaved header edits)
}

function ActionButtons({ onSubmit, submitting, balanced, isDirty, hasSavedCorrections, fromPortal, onBack, readOnly, invoiceStatus, vendorMissing, workflowType, onTroubleshoot, headerIsEditing }: Props) {
  const canApprove = balanced && !isDirty && !submitting && !vendorMissing && !headerIsEditing;
  const canSave    = isDirty && balanced && !submitting;
  const canReject  = !submitting;

  if (readOnly) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 1px 4px rgba(0,56,108,0.08)',
        borderTop: '3px solid #00386C',
      }}>
        <p style={{
          margin: 0,
          fontSize: '13px',
          color: '#4a5568',
          backgroundColor: '#f0f4f8',
          padding: '8px 12px',
          borderRadius: '4px',
          borderLeft: '3px solid #00386C',
        }}>
          This invoice is <strong>{invoiceStatus}</strong> and cannot be edited.
        </p>
        {fromPortal && onBack && (
          <div style={{ marginTop: '16px' }}>
            <button
              onClick={onBack}
              style={{
                padding: '10px 24px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: 'inherit',
                backgroundColor: 'white',
                color: '#4a5568',
                border: '1.5px solid #cbd5e0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              ← Invoice List
            </button>
          </div>
        )}
      </div>
    );
  }

  let statusMessage = '';
  if (headerIsEditing)           statusMessage = 'Header is being edited. Save or cancel header changes before approving.';
  else if (isDirty && !balanced) statusMessage = 'GL lines are not balanced. Balance lines before saving corrections.';
  else if (isDirty)              statusMessage = 'You have unsaved corrections. Save corrections before approving.';
  else if (!balanced)            statusMessage = 'GL lines are not balanced. You may reject but cannot approve.';
  else if (vendorMissing)        statusMessage = 'Vendor not identified — assign a vendor number above before approving.';
  else if (hasSavedCorrections)  statusMessage = 'Corrections saved. You may now approve or reject this invoice.';
  else                           statusMessage = 'GL lines are balanced. You may approve or reject this invoice.';

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 1px 4px rgba(0,56,108,0.08)',
      borderTop: '3px solid #00386C',
    }}>
      <h2 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#00386C', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>
        Actions
      </h2>
      <p style={{
        margin: '0 0 20px 0',
        fontSize: '13px',
        color: (isDirty || headerIsEditing) ? '#b7770d' : '#4a5568',
        backgroundColor: (isDirty || headerIsEditing) ? '#fffbea' : '#f0f4f8',
        padding: '8px 12px',
        borderRadius: '4px',
        borderLeft: `3px solid ${(isDirty || headerIsEditing) ? '#b7770d' : '#00386C'}`,
      }}>
        {statusMessage}
      </p>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

        {/* Approve */}
        <button
          onClick={() => onSubmit('APPROVED')}
          disabled={!canApprove}
          style={{
            padding: '10px 24px',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 700,
            border: 'none',
            fontFamily: 'inherit',
            backgroundColor: canApprove ? '#2d7a2d' : '#e2e8f0',
            color: canApprove ? 'white' : '#a0aec0',
            cursor: canApprove ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'opacity 0.15s',
          }}
        >
          <span style={{ fontSize: 15 }}>✓</span>
          {submitting ? 'Submitting…' : 'Approve'}
        </button>

        {/* Save Corrections */}
        <button
          onClick={() => onSubmit('CORRECTED')}
          disabled={!canSave}
          style={{
            padding: '10px 24px',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 700,
            border: 'none',
            fontFamily: 'inherit',
            backgroundColor: canSave ? '#00386C' : '#e2e8f0',
            color: canSave ? 'white' : '#a0aec0',
            cursor: canSave ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span style={{ fontSize: 14 }}>✎</span>
          {submitting ? 'Saving…' : 'Save Corrections'}
        </button>

        {/* Reject */}
        <button
          onClick={() => onSubmit('REJECTED')}
          disabled={!canReject}
          style={{
            padding: '10px 24px',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 700,
            fontFamily: 'inherit',
            backgroundColor: 'white',
            color: canReject ? '#E33126' : '#a0aec0',
            border: `1.5px solid ${canReject ? '#E33126' : '#e2e8f0'}`,
            cursor: canReject ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span style={{ fontSize: 14 }}>✕</span>
          {submitting ? 'Submitting…' : 'Reject'}
        </button>

        {/* Troubleshoot — OTHER invoices only, admin/power-user context */}
        {workflowType === 'OTHER' && onTroubleshoot && (
          <button
            onClick={onTroubleshoot}
            disabled={submitting}
            title="Run AI diagnosis on this invoice"
            style={{
              padding: '10px 20px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 700,
              fontFamily: 'inherit',
              backgroundColor: 'white',
              color: submitting ? '#a0aec0' : '#6c3483',
              border: `1.5px solid ${submitting ? '#e2e8f0' : '#9b59b6'}`,
              cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            🔧 Troubleshoot
          </button>
        )}

        {/* Back to Invoice List — portal access only */}
        {fromPortal && onBack && (
          <button
            onClick={onBack}
            disabled={submitting}
            style={{
              padding: '10px 24px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 700,
              fontFamily: 'inherit',
              backgroundColor: 'white',
              color: submitting ? '#a0aec0' : '#4a5568',
              border: `1.5px solid ${submitting ? '#e2e8f0' : '#cbd5e0'}`,
              cursor: submitting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            ← Invoice List
          </button>
        )}

      </div>
    </div>
  );
}

export default ActionButtons;
